/**
 * Error Handler Utility
 *
 * Type-safe error handling for catch blocks.
 * Replaces `catch (error: any)` with proper TypeScript types.
 */

import type { ErrorResponse } from '@shared/types/api';

/**
 * API Error with response data
 */
export interface ApiError extends Error {
  response?: {
    data?: ErrorResponse;
    status?: number;
    statusText?: string;
  };
  status?: number;
}

/**
 * Unknown error type (catch-all)
 */
export type UnknownError = unknown;

/**
 * Type guard to check if error is an ApiError with response
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    error instanceof Error &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null
  );
}

/**
 * Type guard to check if error is a standard Error
 */
export function isStandardError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Extract error message from unknown error type
 */
export function getErrorMessage(error: unknown): string {
  // API Error with response
  if (isApiError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (typeof detail === 'object' && detail !== null) {
      return JSON.stringify(detail);
    }
    return error.response?.statusText || error.message || 'API request failed';
  }

  // Standard Error
  if (isStandardError(error)) {
    return error.message;
  }

  // String error
  if (typeof error === 'string') {
    return error;
  }

  // Object with message property
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  // Fallback
  return 'An unknown error occurred';
}

/**
 * Extract error status code from unknown error type
 */
export function getErrorStatus(error: unknown): number | null {
  if (isApiError(error)) {
    return error.response?.status ?? error.status ?? null;
  }
  return null;
}

/**
 * Format error for logging (includes stack trace if available)
 */
export function formatErrorForLogging(error: unknown): string {
  const message = getErrorMessage(error);
  const status = getErrorStatus(error);

  let formatted = `Error: ${message}`;
  if (status !== null) {
    formatted = `[${status}] ${formatted}`;
  }

  if (isStandardError(error) && error.stack) {
    formatted += `\nStack: ${error.stack}`;
  }

  return formatted;
}

/**
 * Error report context for download
 */
export interface ErrorReportContext {
  /** Tab or view name where error occurred */
  tabName?: string;
  /** Additional context data */
  additionalInfo?: Record<string, unknown>;
  /** The raw error object */
  error?: unknown;
  /** Error message string (alternative to error object) */
  errorMessage?: string;
}

/**
 * Generate a detailed error report as a text string
 */
export function generateErrorReport(context: ErrorReportContext): string {
  const timestamp = new Date().toISOString();
  const url = typeof window !== 'undefined' ? window.location.href : 'Unknown';
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown';

  const lines: string[] = [
    '═══════════════════════════════════════════════════════════════════',
    '                    ERROR REPORT',
    '═══════════════════════════════════════════════════════════════════',
    '',
    '📍 LOCATION',
    '───────────────────────────────────────────────────────────────────',
    `URL: ${url}`,
    `Tab/View: ${context.tabName || 'Unknown'}`,
    `Timestamp: ${timestamp}`,
    '',
    '🖥️ ENVIRONMENT',
    '───────────────────────────────────────────────────────────────────',
    `User Agent: ${userAgent}`,
    `Screen Size: ${typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'Unknown'}`,
    `Online Status: ${typeof navigator !== 'undefined' ? (navigator.onLine ? 'Online' : 'Offline') : 'Unknown'}`,
    '',
    '❌ ERROR DETAILS',
    '───────────────────────────────────────────────────────────────────',
  ];

  // Add error details
  if (context.error) {
    const message = getErrorMessage(context.error);
    const status = getErrorStatus(context.error);

    lines.push(`Message: ${message}`);
    if (status !== null) {
      lines.push(`HTTP Status: ${status}`);
    }

    // Add API error response details
    if (isApiError(context.error) && context.error.response) {
      lines.push('');
      lines.push('API Response:');
      lines.push(`  Status: ${context.error.response.status}`);
      lines.push(`  Status Text: ${context.error.response.statusText || 'N/A'}`);
      if (context.error.response.data) {
        lines.push(`  Response Data: ${JSON.stringify(context.error.response.data, null, 2)}`);
      }
    }

    // Add stack trace
    if (isStandardError(context.error) && context.error.stack) {
      lines.push('');
      lines.push('Stack Trace:');
      lines.push(context.error.stack);
    }
  } else if (context.errorMessage) {
    lines.push(`Message: ${context.errorMessage}`);
  }

  // Add additional context
  if (context.additionalInfo && Object.keys(context.additionalInfo).length > 0) {
    lines.push('');
    lines.push('📋 ADDITIONAL CONTEXT');
    lines.push('───────────────────────────────────────────────────────────────────');
    for (const [key, value] of Object.entries(context.additionalInfo)) {
      const formattedValue = typeof value === 'object'
        ? JSON.stringify(value, null, 2)
        : String(value);
      lines.push(`${key}: ${formattedValue}`);
    }
  }

  lines.push('');
  lines.push('═══════════════════════════════════════════════════════════════════');
  lines.push('                    END OF ERROR REPORT');
  lines.push('═══════════════════════════════════════════════════════════════════');

  return lines.join('\n');
}

/**
 * Generate a filename for the error report
 * Format: YYYY-MM-DD_HH-MM-SS_TabName_error.txt
 */
export function generateErrorFilename(tabName?: string): string {
  const now = new Date();
  const date = now.toISOString().slice(0, 10); // YYYY-MM-DD
  const time = now.toTimeString().slice(0, 8).replace(/:/g, '-'); // HH-MM-SS
  const sanitizedTabName = (tabName || 'Unknown')
    .replace(/[^a-zA-Z0-9]/g, '_') // Replace non-alphanumeric with underscore
    .replace(/_+/g, '_') // Replace multiple underscores with single
    .slice(0, 30); // Limit length

  return `${date}_${time}_${sanitizedTabName}_error.txt`;
}

/**
 * Download error report as a TXT file
 */
export function downloadErrorReport(context: ErrorReportContext): void {
  const report = generateErrorReport(context);
  const filename = generateErrorFilename(context.tabName);

  // Create blob and download
  const blob = new Blob([report], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  URL.revokeObjectURL(url);
}
